import React, { useEffect, useState } from "react";
import { Button, Header, Icon, Table, Pagination, Segment, Label, Grid, Input, Modal } from "semantic-ui-react";
import { toast } from "react-toastify";
import useReferenceGroupService from "../../../services/backoffice/hooks/useReferenceGroupService"; // Serviço de Reference Group
import { ToasterConfig } from "../../../config/ToasterConfig";
import TooltipInfo from "../../tooltip/tooltip";
import { SessionUser } from "../../../models/session-user";
import SessionStorage from "../../../store/session";
import { useNavigate } from "react-router-dom";
import ReferenceGroupForm from "./create-reference-group-form";
import {ReferenceGroup} from "../../../models/fleet-type";
import {ReferenceGroupFilters} from "../../../filters/reference-group-filters"; // Formulário de criação/edição de Reference Group

interface ReferenceGroupsListProps {
    isModal?: boolean;
    open?: boolean;
    onClose?: () => void;
}

const ReferenceGroupsList: React.FC<ReferenceGroupsListProps> = ({ isModal = false, open = false, onClose }) => {
    const [referenceGroups, setReferenceGroups] = useState<ReferenceGroup[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string | null>(null);
    const [filter, setFilter] = useState<ReferenceGroupFilters>({ Page: 1 });
    const [totalPages, setTotalPages] = useState<number>(0);
    const { getReferenceGroups, deleteReferenceGroup, activateReferenceGroup, deactivateReferenceGroup } = useReferenceGroupService();
    const [sessionUser, setSessionUser] = useState<SessionUser>();
    const navigate = useNavigate();
    const [showFilters, setShowFilters] = useState(false);

    const [modalOpen, setModalOpen] = useState(false);
    const [editReferenceGroupId, setEditReferenceGroupId] = useState<string | null>(null);

    const toggleFilters = () => {
        setShowFilters(!showFilters);
    };

    useEffect(() => {
        async function updateSessionUser() {
            const user = await SessionStorage.getSession();
            setSessionUser(user);
        }
        updateSessionUser();
    }, []);

    useEffect(() => {
        async function loadReferenceGroups() {
            setLoading(true);
            try {
                const response = await getReferenceGroups(filter);
                setReferenceGroups(response.itens);
                setTotalPages(response.totalPages);
            } catch (err: any) {
                setError("Erro ao carregar grupos de referência.");
            } finally {
                setLoading(false);
            }
        }
        loadReferenceGroups();
    }, [filter]);

    const handleDelete = async (id: string) => {
        try {
            const result = window.confirm("Tem certeza que deseja excluir este grupo de referência?");
            if (result) {
                await deleteReferenceGroup(id);
                toast.success("Grupo de referência excluído com sucesso", ToasterConfig.success);
                setReferenceGroups(referenceGroups.filter((rg) => rg.id !== id));
            }
        } catch (err: any) {
            toast.error("Erro ao excluir grupo de referência", ToasterConfig.error);
        }
    };

    const handleActivation = async (referenceGroup: ReferenceGroup) => {
        try {
            const result = window.confirm(`Tem certeza que deseja ${referenceGroup.isActive ? 'desativar' : 'ativar'} este grupo de referência?`);
            if (result) {
                if (referenceGroup.isActive) {
                    await deactivateReferenceGroup(referenceGroup.id);
                } else {
                    await activateReferenceGroup(referenceGroup.id);
                }
                setFilter({ ...filter }); // Atualiza a lista
                toast.success(`Grupo de referência ${referenceGroup.isActive ? 'desativado' : 'ativado'} com sucesso.`, ToasterConfig.success);
            }
        } catch (err: any) {
            toast.error("Erro ao alterar status do grupo de referência", ToasterConfig.error);
        }
    };

    const handleEdit = (id: string) => {
        setEditReferenceGroupId(id);
        setModalOpen(true);
    };

    // Formulário de grupos de referência com botão de "Novo"
    const formContent = (
        <Segment raised style={{ maxWidth: "900px", margin: "5px auto", padding: "20px" }}>
            <Header as="h3" textAlign="center" color={'green'}>
                Grupos de Referência
            </Header>
            <Table.Row>
                <Table.HeaderCell>
                    {sessionUser?.userData?.profile === 'Admin' && (
                        <Button color="green" onClick={() => setModalOpen(true)}>
                            <Icon name='plus' /> Novo
                        </Button>
                    )}
                </Table.HeaderCell>
                <Table.HeaderCell colSpan="4">
                    <Button onClick={toggleFilters} color="green">
                        <Icon name={showFilters ? 'hide' : 'unhide'} />
                        {showFilters ? 'Ocultar filtros' : 'Exibir filtros'}
                    </Button>
                </Table.HeaderCell>
            </Table.Row>

            {showFilters && (
                <Segment>
                    <Grid stackable doubling padded>
                        <Grid.Row>
                            <Grid.Column width={6}>
                                <label htmlFor="code">Código</label>
                                <Input
                                    fluid
                                    id="code"
                                    icon="search"
                                    type="text"
                                    placeholder="Código do grupo de referência"
                                    value={filter?.code ?? ''}
                                    onChange={(event) => setFilter({ ...filter, code: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={6}>
                                <label htmlFor="name">Nome do Grupo</label>
                                <Input
                                    fluid
                                    id="name"
                                    icon="search"
                                    type="text"
                                    placeholder="Nome do grupo de referência"
                                    value={filter?.name ?? ''}
                                    onChange={(event) => setFilter({ ...filter, name: event.target.value })}
                                />
                            </Grid.Column>

                            <Grid.Column width={2} verticalAlign="bottom">
                                <Button color="green" onClick={() => setFilter({ ...filter, Page: 1 })}>Buscar</Button>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                </Segment>
            )}

            <Table color="green">
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>#</Table.HeaderCell>
                        <Table.HeaderCell>Código</Table.HeaderCell>
                        <Table.HeaderCell>Nome</Table.HeaderCell>
                        <Table.HeaderCell>Ativo</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {referenceGroups?.map((referenceGroup) => (
                        <Table.Row key={referenceGroup.id}>
                            <Table.Cell>
                                <Button.Group>
                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Editar grupo de referência"
                                            trigger={
                                                <Button
                                                    color="green"
                                                    onClick={() => {
                                                        setEditReferenceGroupId(referenceGroup.id);
                                                        setModalOpen(true);
                                                    }}
                                                    icon="edit"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content="Excluir grupo de referência"
                                            trigger={
                                                <Button
                                                    color="red"
                                                    onClick={() => handleDelete(referenceGroup.id)}
                                                    icon="trash"
                                                />
                                            }
                                        />
                                    )}

                                    {sessionUser?.userData?.profile === 'Admin' && (
                                        <TooltipInfo
                                            content={referenceGroup.isActive ? "Desativar grupo de referência" : "Ativar grupo de referência"}
                                            trigger={
                                                <Button
                                                    color={referenceGroup.isActive ? 'red' : 'green'}
                                                    onClick={() => handleActivation(referenceGroup)}
                                                    icon={referenceGroup.isActive ? 'power off' : 'power'}
                                                />
                                            }
                                        />
                                    )}
                                </Button.Group>
                            </Table.Cell>
                            <Table.Cell>{referenceGroup.code}</Table.Cell>
                            <Table.Cell>{referenceGroup.name}</Table.Cell>
                            <Table.Cell>
                                <Label color={referenceGroup.isActive ? 'green' : 'red'}>
                                    {referenceGroup.isActive ? 'Sim' : 'Não'}
                                </Label>
                            </Table.Cell>
                        </Table.Row>
                    ))}
                </Table.Body>
            </Table>

            <Pagination
                totalPages={totalPages}
                defaultActivePage={filter?.Page ?? 1}
                onPageChange={(e, { activePage }) => setFilter({ ...filter, Page: activePage as number })}
            />
        </Segment>
    );

    return (
        <>
            <Modal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                size="tiny"
                style={{ width: '620px', maxWidth: '95vw', padding: '20px' }}
            >
                <ReferenceGroupForm
                    isModal
                    open={modalOpen}
                    referenceGroupId={editReferenceGroupId || undefined}
                    onClose={() => {
                        setModalOpen(false);
                        setEditReferenceGroupId(null);
                        setFilter({ ...filter });
                    }}
                />
            </Modal>

            {formContent}
        </>
    );
};

export default ReferenceGroupsList;
